import React, { useEffect, useMemo, useState } from 'react';
import OrgOnboardingPage from './OrgOnboardingPage';
import onboardingMessages from '@/messages/onboarding';
import { Box, Center, HStack, Image, Text, Tooltip, VStack } from '@chakra-ui/react';
import { TABLE_COLUMNS_ORG_ROSTER_REGISTER } from '@/utils/tableConfigs';
import { TParsedRosterList, useOrganizationOnboardingStore } from '@/store/useOrganizationOnboardingStore';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { USER_AFFILIATION_OPTIONS } from '@/constants';
import CustomTable from '@/components/molecules/CustomTable';
import { TRosterItemPerson } from '@/types/Roster.types';
import { TUserAffiliation } from '@/types/User.types';
import { isUserMarkedAsPeerSupport } from '@/utils/onboardingUtils';
import iconsPng from '@/assets/img/png/icons';

const COLUMNS: string[] = ['Name', 'Website', 'Assign'];
type TSortingColumn = (typeof COLUMNS)[number];

type Props = {
  onGoBack?: () => void;
  onSubmit?: () => void;
  isDisabledSubmit?: boolean;
  isLoading?: boolean;
  rosterList?: TParsedRosterList;
  headerHidden?: boolean;
};

const RosterPreview = ({
  onGoBack = () => {},
  onSubmit = () => {},
  isDisabledSubmit = false,
  isLoading = false,
  rosterList,
  headerHidden,
}: Props) => {
  const { rosterCSV, rosterManual, selectedRosterTab } = useOrganizationOnboardingStore();

  const [rosterRowIndexesWithErrors, setRosterRowIndexesWithErrors] = useState<number[]>([]);

  const finalRoster = rosterList ? rosterList : selectedRosterTab === 'csv' ? rosterCSV : rosterManual;

  // scroll, normalize, report
  useEffect(() => {
    // scrolls to top
    window.scrollTo({ top: 0, behavior: 'instant' });

    // reports whether Affiliation is added correctly in every row
    const rowIndexesWithErrors: number[] = [];

    finalRoster?.forEach((rosterItem, index) => {
      if (!USER_AFFILIATION_OPTIONS.includes(rosterItem.Affiliation.toLowerCase() as TUserAffiliation)) {
        rowIndexesWithErrors.push(index);
      }
    });

    setRosterRowIndexesWithErrors(rowIndexesWithErrors);
  }, []);

  const isDisabledSubmitFinal = isDisabledSubmit || rosterRowIndexesWithErrors.length > 0;

  const rosterItemsWithId = (finalRoster ?? []).map((rosterItem) => ({
    ...rosterItem,
    id: rosterItem.Email,
  }));

  const tableListItems = useMemo(
    () =>
      rosterItemsWithId.map((item: (typeof rosterItemsWithId)[number]) => {
        return {
          id: item.id,
          cells: TABLE_COLUMNS_ORG_ROSTER_REGISTER.map((column) => {
            if (column.label === 'PEER SUPPORT') {
              return (
                <HStack>
                  <Text fontWeight={600}>{isUserMarkedAsPeerSupport(item['Peer Support']) ? 'Yes' : 'No'}</Text>
                  {item['Peer Support Lead'] ? (
                    <Tooltip
                      label={'Peer Support Lead'}
                      color={'text.darkBlue'}
                      fontWeight={600}
                      background={'white'}
                      maxWidth={'140px'}
                      borderRadius={'8px'}
                      padding={'10px'}
                      placement="bottom-start"
                      left={5}
                    >
                      <Image
                        alt="peer-support-lead"
                        src={iconsPng.peerSupportLead}
                        width={4}
                        height={5}
                        opacity={item['Peer Support Lead'] ? 0.5 : 0}
                        _hover={{ opacity: item['Peer Support Lead'] ? 1 : 0 }}
                      />
                    </Tooltip>
                  ) : null}
                </HStack>
              );
            } else {
              return column.renderCell(item);
            }
          }),
        };
      }),
    [rosterItemsWithId],
  );

  return (
    <OrgOnboardingPage
      title={onboardingMessages.previewYourRosterTitle}
      subTitle={onboardingMessages.previewYourRosterSubtitle}
      description={onboardingMessages.previewYourRosterDescription}
      onGoBack={onGoBack}
      onSubmit={onSubmit}
      submitLabel={onboardingMessages.looksGood}
      isSubmitDisabled={isDisabledSubmitFinal}
      isSubmitLoading={isLoading}
      hideSeparator={true}
      headerHidden={headerHidden}
    >
      <Box width={MAX_CONTAINER_WIDTH} marginY={'50px'}>
        {rosterRowIndexesWithErrors.length > 0 && (
          <Center>
            <VStack justify={'center'} marginBottom={'20px'}>
              <Text variant={'error'} fontSize={18} height={'20px'}>
                Some entries contain Affiliation errors. Make sure each Affiliation is one of the following values:
              </Text>
              <Text variant={'error'} fontSize={18} height={'20px'} marginBottom={'10px'} whiteSpace={'pre'}>
                {USER_AFFILIATION_OPTIONS.slice(0, 3).join(', ')} or {USER_AFFILIATION_OPTIONS[3]}.
              </Text>
            </VStack>
          </Center>
        )}

        <Center>
          <CustomTable<TRosterItemPerson[], TSortingColumn>
            dataWithId={rosterItemsWithId}
            tableListItems={tableListItems}
            invalidatedItemIndexes={rosterRowIndexesWithErrors}
            customColumnGridPercentages={[13, 15, 13, 30, 17, 12]}
            columns={TABLE_COLUMNS_ORG_ROSTER_REGISTER.map((column) => column.label)}
          />
        </Center>
      </Box>
    </OrgOnboardingPage>
  );
};

export default RosterPreview;
